import { CommonModule } from "@angular/common";
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import * as L from "leaflet";

export interface IMapMarker {
  id: string;
  lat: number;
  lng: number;
  title?: string;
  popup?: string; // You can use HTML (e.g. with <br> in the code).
  icon?: L.Icon | L.DivIcon;
}

/**
 * Documentation : https://leafletjs.com/
 */
@Component({
  selector: "app-map",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./map.component.html",
  styleUrls: ["./map.component.scss"],
})
export class MapComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() markerIdClick = new EventEmitter<string>();

  @Input() latitude?: number; // Optional: forces centring
  @Input() longitude?: number; // Optional: forces centring
  @Input() zoom = 13;
  @Input() markers: IMapMarker[] = []; // !!! If latitude and longitude are not provided, markers must be a non-empty array. !!!
  @Input() height = "400px";
  @Input() enableGeolocation = false;
  @Input() showMainMarker = true; // Display the main marker if latitude/longitude are provided
  @Input() connectCoordinates = false; // Connect the coordinates with a line

  private map!: L.Map;
  private markersLayer: { marker: L.Marker; id?: string }[] = [];

  ngOnInit(): void {
    this.fixLeafletIconPath();
  }

  ngAfterViewInit(): void {
    this.initMap();
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
    }
  }

  /**
   * Initializes the Leaflet map
   */
  private initMap(): void {
    const center = this.getMapCenter();

    // init the map
    this.map = L.map("map", {
      center: center,
      zoom: this.zoom,
    });

    // add layer openstreetmap (you can change it and/or add several)
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "© OpenStreetMap contributors",
    }).addTo(this.map);

    // add markers
    this.renderMarkers();

    if (this.enableGeolocation) {
      this.addGeolocation();
    }

    // Adjust the view to include all points (only if no manual lat/lng)
    if (!this.latitude || !this.longitude) {
      this.fitBounds();
    }
  }

  /**
   * Computes map center from inputs or markers
   * @return LatLng center of the map
   */
  private getMapCenter(): L.LatLngExpression {
    // If latitude and longitude are provided, use them.
    if (this.latitude !== undefined && this.longitude !== undefined) {
      return [this.latitude, this.longitude];
    }

    // Otherwise, calculate from the markers.
    const avgLat = this.markers.reduce((sum, marker) => sum + marker.lat, 0) / this.markers.length;
    const avgLng = this.markers.reduce((sum, marker) => sum + marker.lng, 0) / this.markers.length;
    return [avgLat, avgLng];
  }

  /**
   * Renders all markers on the map
   */
  private renderMarkers(): void {
    // Supprimer les anciens marqueurs
    this.markersLayer.forEach((m) => m.marker.remove());
    this.markersLayer = [];

    // Ajouter le marqueur principal si latitude/longitude fournis ET showMainMarker activé
    if (this.latitude !== undefined && this.longitude !== undefined && this.showMainMarker) {
      const mainMarker = L.marker([this.latitude, this.longitude]).addTo(this.map).bindPopup("Position principale");
      this.markersLayer.push({ marker: mainMarker });
    }

    // Ajouter les marqueurs supplémentaires
    this.markers.forEach((markerData) => {
      const marker = L.marker([markerData.lat, markerData.lng], {
        icon: markerData.icon || this.getDefaultIcon(),
      }).addTo(this.map);

      marker.bindPopup(markerData.popup ? markerData.popup : `Lat: ${markerData.lat.toFixed(6)} <br>Lng: ${markerData.lng.toFixed(6)}`);

      if (markerData.title) {
        marker.bindTooltip(markerData.title);
      }

      marker.addEventListener("click", this.clickOnMarker.bind(this, markerData.id));

      this.markersLayer.push({ marker: marker, id: markerData.id });
    });
  }

  /**
   * Handles marker click event
   * @param id Marker identifier
   */
  private clickOnMarker(id: string): void {
    this.markerIdClick.emit(id);
  }

  /**
   * Adds user geolocation marker
   */
  private addGeolocation(): void {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        const userMarker = L.marker([lat, lng], {
          icon: this.getUserLocationIcon(),
        })
          .addTo(this.map)
          .bindPopup("Votre position")
          .openPopup();

        this.markersLayer.push({ marker: userMarker });
        this.map.setView([lat, lng], this.zoom);
      });
    }
  }

  /**
   * Fits map bounds to visible markers
   */
  private fitBounds(): void {
    const allPoints: L.LatLngExpression[] = [];

    // Ajouter le point principal si fourni
    if (this.latitude !== undefined && this.longitude !== undefined && this.showMainMarker) {
      allPoints.push([this.latitude, this.longitude]);
    }

    // Ajouter les marqueurs personnalisés
    this.markers.forEach((marker) => {
      allPoints.push([marker.lat, marker.lng]);
    });

    // Ajuster la vue si on a un point
    if (allPoints.length === 1) {
      this.map.setView(allPoints[0], 13);
    }

    // Ajuster la vue si on a plusieurs points
    else if (allPoints.length > 1) {
      const bounds = L.latLngBounds(allPoints);
      this.map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
    }
  }

  /**
   * Returns default marker icon.
   * @return Leaflet icon
   */
  private getDefaultIcon(): L.Icon {
    return L.icon({
      iconUrl: "assets/marker-icon.png",
      shadowUrl: "assets/marker-shadow.png",
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    });
  }

  /**
   * Returns user location icon
   * @return Leaflet div icon
   */
  private getUserLocationIcon(): L.DivIcon {
    return L.divIcon({
      className: "user-location-icon",
      html: '<div style="background-color: #4285F4; width: 16px; height: 16px; border-radius: 50%; border: 3px solid white; box-shadow: 0 0 6px rgba(0,0,0,0.3);"></div>',
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });
  }

  /**
   * Fixes default Leaflet icon asset paths
   */
  private fixLeafletIconPath(): void {
    const iconRetinaUrl = "assets/marker-icon-2x.png";
    const iconUrl = "assets/marker-icon.png";
    const shadowUrl = "assets/marker-shadow.png";
    const iconDefault = L.icon({
      iconRetinaUrl,
      iconUrl,
      shadowUrl,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      tooltipAnchor: [16, -28],
      shadowSize: [41, 41],
    });
    L.Marker.prototype.options.icon = iconDefault;
  }

  // Public methods for controlling the card from the outside
  /**
   * Centers map on coordinates
   * @param lat Latitude
   * @param lng Longitude
   * @param zoom Optional zoom level
   */
  public centerMap(lat: number, lng: number, zoom?: number): void {
    if (this.map) {
      this.map.setView([lat, lng], zoom || this.zoom);
    }
  }

  /**
   * Center the map such that it fits all markers
   */
  public centerMarkers(): void {
    if (this.map) {
      this.fitBounds();
    }
  }

  /**
   * Adds markers to the map
   * @param markers List of markers
   */
  public addMarkers(markers: IMapMarker[]): void {
    markers.forEach((marker) => this.markers.push(marker));
    if (this.map) {
      this.renderMarkers();
    }
  }

  /**
   * Removes all markers from the map
   */
  public clearMarkers(): void {
    this.markersLayer.forEach((m) => m.marker.remove());
    this.markersLayer = [];
    this.markers = [];
  }

  /**
   * Opens popup for a marker
   * @param id Marker identifier
   */
  public openMarkerPopup(id: string): void {
    if (!this.map) return;

    const found = this.markersLayer.find((m) => m.id === id);
    if (!found) return;

    found.marker.openPopup();
  }
}

// ============================================
// Exemple d'utilisation
// ============================================
/*
// CAS 1 : Liste de coordonnées uniquement (centrage automatique)
<app-map
  [coordinates]="gpsCoordinates"
  [connectCoordinates]="true">
</app-map>

gpsCoordinates: Coordinate[] = [
  { lat: 50.8503, lng: 4.3517 },
  { lat: 50.8467, lng: 4.3525 },
  { lat: 50.8450, lng: 4.3580 }
];

// CAS 2 : Forcer le centrage avec latitude/longitude
<app-map
  [latitude]="50.8503"
  [longitude]="4.3517"
  [coordinates]="gpsCoordinates"
  [showMainMarker]="true">
</app-map>

// CAS 3 : Centrage manuel sans marqueur principal
<app-map
  [latitude]="50.8503"
  [longitude]="4.3517"
  [coordinates]="gpsCoordinates"
  [showMainMarker]="false">
</app-map>

// Dans votre TypeScript :
import { Coordinate } from './map/map.component';

gpsCoordinates: Coordinate[] = [
  { lat: 50.8503, lng: 4.3517 },
  { lat: 50.8467, lng: 4.3525 },
  { lat: 50.8450, lng: 4.3580 },
  { lat: 50.8470, lng: 4.3600 }
];

// Ajouter dynamiquement des coordonnées
@ViewChild(MapComponent) mapComponent!: MapComponent;

addNewCoordinate() {
  this.mapComponent.addCoordinate({ lat: 50.8500, lng: 4.3550 });
}
*/
